import type { IPlace, ParsedLicense } from "./types";

/** Minimal interface; real SurveyJS SurveyModel satisfies it. */
interface SurveyLike {
  getQuestionByName(name: string): { value: unknown } | null;
}

export const DEFAULT_FIELD_PREFIX = "person-reportingParty_";

type ScalarKey =
  | "firstName"
  | "lastName"
  | "dob"
  | "sex"
  | "driverLicenseNo"
  | "driverLicenseJurisdiction";

const SCALAR_KEYS: ScalarKey[] = [
  "firstName",
  "lastName",
  "dob",
  "sex",
  "driverLicenseNo",
  "driverLicenseJurisdiction",
];

export interface ApplyResult {
  filledFields: string[];
}

export function applyParsedLicenseToSurvey(
  parsed: ParsedLicense,
  place: IPlace | null,
  survey: SurveyLike,
  fieldPrefix: string = DEFAULT_FIELD_PREFIX,
): ApplyResult {
  const prefix = normalizePrefix(fieldPrefix);
  const filled: string[] = [];

  for (const key of SCALAR_KEYS) {
    const value = parsed[key];
    if (value === undefined || value === null || value === "") continue;
    const target = `${prefix}${key}`;
    const question = survey.getQuestionByName(target);
    if (!question) continue;
    question.value = value;
    filled.push(target);
  }

  if (place) {
    filled.push(...applyPlaceToSurvey(place, survey, prefix).filledFields);
  }

  return { filledFields: filled };
}

/**
 * Write only the Google place into the address/location question. Split out
 * from applyParsedLicenseToSurvey so the scanner can fill the scalar fields
 * immediately on confirm and fill the address later, once the (async,
 * best-effort) reverse-geocode resolves — without re-touching scalar fields
 * the user may have edited in the meantime.
 */
export function applyPlaceToSurvey(
  place: IPlace,
  survey: SurveyLike,
  fieldPrefix: string = DEFAULT_FIELD_PREFIX,
): ApplyResult {
  const prefix = normalizePrefix(fieldPrefix);
  const target = `${prefix}location`;
  const question = survey.getQuestionByName(target);
  if (!question) return { filledFields: [] };
  question.value = { googleMapsPlace: place };
  return { filledFields: [target] };
}

function normalizePrefix(fieldPrefix: string): string {
  return typeof fieldPrefix === "string" && fieldPrefix.length > 0
    ? fieldPrefix
    : DEFAULT_FIELD_PREFIX;
}
